import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch } from 'react-redux';
import { TJobRequestExtraRate } from '../../../operations/shifts/AgentShiftForm';
import { updateField } from '../../../../actions/BookingRequest';
import { ErrorMessage } from '../../../common';

export type TModalAddExtraRatesProps = {
    extraRatesFromAPI: TExtraRate[];
    extraRates: TJobRequestExtraRate[];
    setExtraRates: any;
    handleClose: () => void;
    isOpen: boolean;
};

export function ModalAddExtraRates({
    extraRatesFromAPI,
    extraRates,
    setExtraRates,
    handleClose,
    isOpen,
}: TModalAddExtraRatesProps) {
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const [fields, setFields] = useState([]);
    const [fieldsAreValid, setFieldsAreValid] = useState(true);

    useEffect(() => {
        setFields(extraRates.map(rate => ({
            freelancer_pay_per_hour: rate.freelancer_pay_per_hour || '',
            client_pay_per_hour: rate.client_pay_per_hour || '',
            description: rate.description || '',
        })));
    }, [isOpen]);

    const isEmpty = (fieldsRow: any) =>  
        !fieldsRow.freelancer_pay_per_hour && !fieldsRow.client_pay_per_hour && !fieldsRow.description;

    const isFilled = (fieldsRow: any) => 
        !!fieldsRow.freelancer_pay_per_hour && !!fieldsRow.client_pay_per_hour && !!fieldsRow.description;

    const validateFields = () => {
        let valid = true;
        if (fields.length) {
            fields.forEach((fieldsRow, index) => {
                if (!isEmpty(fieldsRow) && !isFilled(fieldsRow)) {
                    valid = false;
                    Object.keys(fieldsRow).forEach(fieldName => {
                        const field = (document.getElementById(`${fieldName}${index}`) as HTMLInputElement);
                        if (field.value) {
                            field.removeAttribute('required');
                        } else {
                            field.setAttribute('required', '');
                        }
                    });
                } else {
                    Object.keys(fieldsRow).forEach(fieldName => {
                        const field = (document.getElementById(`${fieldName}${index}`) as HTMLInputElement);
                        field.removeAttribute('required');
                    });
                }
            });

        }
        setFieldsAreValid(valid);
    };

    useEffect(() => {
        validateFields();
    }, [fields]);

    const getName = (id: number) => extraRatesFromAPI.find(rate => rate.id === id).name;

    const isFormValid = () => {
        const invalidFields = fields.filter(field => (!isEmpty(field) && !isFilled(field)));
        if (invalidFields.length) {
            const invalidRateNames = invalidFields.map((field, index) => getName(extraRates[index].extra_rate));
            setError(`All fields of '${invalidRateNames.join(', ')}' must be either empty or filled.`);
            return false;
        }

        return true;
    };

    const handleSave = () => {
        if (isFormValid()) {
            const newExtraRates = extraRates.map((extraRate, index) => ({
                ...extraRate,
                freelancer_pay_per_hour: fields[index].freelancer_pay_per_hour,
                client_pay_per_hour: fields[index].client_pay_per_hour,
                description: fields[index].description,
                })
            );
            setExtraRates(newExtraRates);

            const newRates = newExtraRates.filter(ExtraRate =>
                ExtraRate.freelancer_pay_per_hour && ExtraRate.client_pay_per_hour && ExtraRate.description
            );
            dispatch(updateField('rates', newRates));
            handleClose();
        }
    };

    const handleFieldChange = (event: any, index: number, fieldName: string) => {
        const newState = fields.map((fieldsRow: object, fieldsRowIndex) => {
            if (fieldsRowIndex === index) {
                return { ...fieldsRow, [fieldName]: event.target.value };
            }

            return fieldsRow;
        });
        setFields(newState);
    };

    const handleClearFieldsRow = (event: any, index: number) => {
        event.preventDefault();
        const newState = fields.map((fieldsRow: object, fieldsRowIndex) => {
            if (fieldsRowIndex === index) {
                return {
                    freelancer_pay_per_hour: '',
                    client_pay_per_hour: '',
                    description: '',
                };
            }

            return fieldsRow;
        });
        setFields(newState);
    };

    const handleMouseEnter = (event: any) => {
        event.target.className = 'icon icon-remove-circle-11 red';
    };

    const handleMouseLeave = (event: any) => {
        event.target.className = 'icon icon-remove-circle-1 red';
    };

    return (
        <Modal
            className="add-extra-rates-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Add Extra Rates"
            ariaHideApp={false}
        >
            <h4>Add Extra Rates</h4>
            <form className="extra-rates-form">
                <table>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Workers rate</th>
                            <th>Client rate</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((fieldsRow, index) => (
                            <tr key={extraRates[index].extra_rate}>
                                <td>
                                    {getName(extraRates[index].extra_rate)}
                                </td>
                                <td className="extra-rates-number-field">
                                    <input
                                        id={`freelancer_pay_per_hour${index}`}
                                        type="number"
                                        step="0.25"
                                        value={fieldsRow.freelancer_pay_per_hour}
                                        onChange={event => {
                                            handleFieldChange(event, index, 'freelancer_pay_per_hour');
                                        }}
                                        disabled={!extraRatesFromAPI[index].is_active}
                                    />
                                </td>
                                <td className="extra-rates-number-field">
                                    <input
                                        id={`client_pay_per_hour${index}`}
                                        type="number"
                                        step="0.25"
                                        value={fieldsRow.client_pay_per_hour}
                                        onChange={event => {
                                            handleFieldChange(event, index, 'client_pay_per_hour');
                                        }}
                                        disabled={!extraRatesFromAPI[index].is_active}
                                    />
                                </td>
                                <td className="extra-rates-description-field">
                                    <input
                                        id={`description${index}`}
                                        type="text"
                                        value={fieldsRow.description}
                                        onChange={event => {
                                            handleFieldChange(event, index, 'description');
                                        }}
                                        disabled={!extraRatesFromAPI[index].is_active}
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        onClick={event =>{
                                            handleClearFieldsRow(event, index);
                                        }}
                                        disabled={!extraRatesFromAPI[index].is_active}
                                    >
                                        <i
                                            className={`icon icon-remove-circle-1 ${extraRatesFromAPI[index].is_active ? 'red' : 'grey'}`}
                                            onMouseEnter={extraRatesFromAPI[index].is_active ? handleMouseEnter : null}
                                            onMouseLeave={extraRatesFromAPI[index].is_active ? handleMouseLeave : null}
                                        />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        disabled={!fieldsAreValid}
                        onClick={handleSave}
                        type="button"
                    >
                        SAVE
                    </button>
                    <button
                        className="btn cancel-btn"
                        onClick={handleClose}
                        type="button"
                    >
                        CANCEL
                    </button>
                </div>
            </form>
            {error && <ErrorMessage message={error}/>}
        </Modal>
    );
}

type TExtraRate = {
    id: number;
    name: string;
    is_active: boolean;
}

type TExtraRatesProps = {
    extraRatesFromAPI: TExtraRate[];
    extraRates: TJobRequestExtraRate[];
    setExtraRates: any;
};

export default function ExtraRates({
    extraRatesFromAPI,
    extraRates,
    setExtraRates,
}: TExtraRatesProps) {
    const [isModalAddExtraRatesOpen, setIsModalAddExtraRatesOpen] = useState(false);

    const handleOpenAddExtraRatesModal = () => {
        setIsModalAddExtraRatesOpen(true);
    };

    const handleCloseAddExtraRatesModal = () => {
        setIsModalAddExtraRatesOpen(false);
    };

    const extraRatesSelected =
        extraRates?.filter(rate =>
            !!rate.freelancer_pay_per_hour && !!rate.client_pay_per_hour && !!rate.description).length;

    return (
        <div>
            <a onClick={handleOpenAddExtraRatesModal}>
                {extraRatesSelected ? 'Check or change extra rates...' : 'Add extra rates...'}
            </a>
            {isModalAddExtraRatesOpen && (
                <ModalAddExtraRates
                    extraRatesFromAPI={extraRatesFromAPI}
                    extraRates={extraRates}
                    setExtraRates={setExtraRates}
                    handleClose={handleCloseAddExtraRatesModal}
                    isOpen={isModalAddExtraRatesOpen}
                />
            )}
        </div>
    );
}
